import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { UserRoleType } from '../../../../shared/models/entities/user-role';
import { addMonths, endOfDay, startOfDay, startOfYear, addYears, endOfYear } from 'date-fns';
import { ReportingVehicleFleet } from '../../../../shared/models/api/reportings/reporting-vehicle-fleet';
import { PagedResponse } from '../../../../shared/models/api/shared/paged/paged-response.interface';
import { NzTableSortOrder, NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subscription } from 'rxjs';
import { ReportingsService } from '../../../../shared/services/api/reportings.service';
import { UserService } from '../../../../shared/services/api/user.service';
import { Title } from '@angular/platform-browser';
import { PagedRequest } from '../../../../shared/models/api/shared/paged/paged-request';
import { ReportingVehicleFleetFiltersRequest } from '../../../../shared/models/api/services/reporting-vehicle-fleet-filters-requests.interface';
import { Utils } from '../../../../shared/utils/utils';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ApiErrorMessageUtil } from '../../../../shared/utils/api-error-message.util';
import { take } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'laveo-reporting-fleet',
  templateUrl: './reporting-fleet.component.html',
  styleUrls: ['./reporting-fleet.component.scss']
})
export class ReportingFleetComponent implements OnInit, OnDestroy {
  @ViewChild('exportForm') private exportFormContent: TemplateRef<any>;

  isLoading = true;
  actionLoading = false;
  currentPage = 1;
  limit = 10;

  form: UntypedFormGroup;
  exportForm: UntypedFormGroup;
  userCanChooseSelectedRole = false;
  selectedRole: UserRoleType.customerSite | UserRoleType.structure = UserRoleType.customerSite;
  UserRoleType = UserRoleType;
  minDate = addMonths(startOfDay(new Date()), -6);
  maxDate = endOfDay(new Date());
  dateRanges: Record<string, Date[]> = {};

  error?: Error;

  data?: PagedResponse<ReportingVehicleFleet>;

  private sort: { key: string; value: NzTableSortOrder }[] = [{ key: 'customer', value: 'ascend' }];
  private subscriptions: Subscription[] = [];

  constructor(
    private readonly reportingsService: ReportingsService,
    private readonly userService: UserService,
    private readonly titleService: Title,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService
  ) {
    const currentYear = new Date().getFullYear();
    this.dateRanges[currentYear] = [startOfYear(new Date()), endOfYear(new Date())];
    this.dateRanges[currentYear - 1] = [startOfYear(addYears(new Date(), -1)), endOfYear(addYears(new Date(), -1))];
    this.dateRanges[currentYear - 2] = [startOfYear(addYears(new Date(), -2)), endOfYear(addYears(new Date(), -2))];
  }

  get description(): string | undefined {
    return undefined;
  }

  ngOnInit(): void {
    this.setTitle();
    this.setForm();
    this.loadRole();
    this.loadData();
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  loadData(): void {
    let sortProperty: string | undefined;
    let sortType: 'ASC' | 'DESC' | undefined;

    const currentSort =  this.sort.find(s => s.value);
    if (currentSort) {
      sortProperty = currentSort.key;
      sortType = currentSort.value === 'ascend' ? 'ASC' : 'DESC';
    }

    const parameters = new PagedRequest<ReportingVehicleFleetFiltersRequest>({
      page: this.currentPage,
      limit: this.limit,
      sortProperty,
      sortType,
      filters: {
        type: this.selectedRole,
        minDate: this.minDate,
        maxDate: this.maxDate
      },
    });

    this.isLoading = true;
    this.error = undefined;

    const reportingsSubscription = this.reportingsService.reportingVehicleFleets(parameters).subscribe({
      next: data => {
        this.data = data.data;
        this.isLoading = data.loading;
      },
      error: error => {
        this.isLoading = false;
        console.error(error);
        this.error = error;
      }
    });

    this.subscriptions.push(reportingsSubscription);
  }

  setPage(event: NzTableQueryParams): void {
    const indexSame = !event.pageIndex || this.currentPage === event.pageIndex;
    const limitSame = this.limit === event.pageSize;
    let sortSame = true;
    for (const sortObject of event.sort) {
      const originalSort = this.sort.find(sortElement => sortElement.key === sortObject.key);
      if (originalSort?.value !== sortObject.value) {
        sortSame = false;
        break;
      }
    }

    if (indexSame && limitSame && sortSame) {
      return;
    }

    this.currentPage = event.pageIndex;
    this.limit = event.pageSize;
    this.sort = event.sort;
    this.loadData();
  }

  back(): void {
    window.history.back();
  }

  export(): void {
    this.exportForm = this.formBuilder.group({
      role: this.formBuilder.control(this.form.get('role')?.value, [Validators.required]),
      dates: this.formBuilder.control(this.form.get('dates')?.value, [Validators.required]),
      page: this.formBuilder.control('all', [Validators.required]),
      filename: this.formBuilder.control('')
    });

    const roleSubscription = this.exportForm.get('role')?.valueChanges.subscribe(role => {
      this.form.get('role')?.setValue(role);
      this.selectedRole = role;
      this.loadData();
    });

    if (roleSubscription) {
      this.subscriptions.push(roleSubscription);
    }

    const datesSubscription = this.exportForm.get('dates')?.valueChanges.subscribe(dates => {
      this.form.get('dates')?.setValue(dates);
      this.minDate = dates[0];
      this.maxDate = dates[1];
      this.loadData();
    });

    if (datesSubscription) {
      this.subscriptions.push(datesSubscription);
    }

    this.modal.create({
      nzTitle: 'Exporter en .xlsx',
      nzContent: this.exportFormContent,
      nzOkText: 'Exporter',
      nzCancelText: 'Annuler',
      nzData: {
        exportForm: this.exportForm
      },
      nzOkDisabled: !this.exportForm.valid,
      nzOnOk: () => {
        let filename: string = this.exportForm.get('filename')?.value ?? 'export';
        filename = filename.trim() === '' ? 'export' : filename.trim();
        filename = filename.toLowerCase().replaceAll(' ', '-');

        const dates: Date[] = this.exportForm.get('dates')?.value;
        const onlyCurrentPage = (this.exportForm.get('page')?.value ?? 'current') === 'current';

        const pagedRequest = new PagedRequest<ReportingVehicleFleetFiltersRequest>({
          page: onlyCurrentPage ? (this.currentPage ? +this.currentPage : 1) : undefined,
          limit: onlyCurrentPage ? 10 : undefined,
          filters: {
            minDate: dates[0] ?? this.minDate,
            maxDate: dates[1] ?? this.maxDate,
            type: this.exportForm.get('role')?.value
          },
          sortType: this.exportForm.get('sort')?.value
        });

        this.reportingsService.reportingVehicleFleetsExcel(pagedRequest).pipe(take(1)).subscribe({
          next: (url => {
            if (url.data) {
              Utils.download(url.data, filename);
            } else {
              this.message.error('Une erreur s\'est produite durant l\'export. Réessayez plus tard');
            }
          }),
          error: (error => {
            console.error(error);
            this.message.error(ApiErrorMessageUtil.getMessageFromError(error));
          })
        });
      }
    });
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      role: this.formBuilder.control(this.selectedRole),
      dates: this.formBuilder.control([this.minDate, this.maxDate]),
    });

    const roleSubscription = this.form.get('role')?.valueChanges.subscribe(role => {
      this.selectedRole = role;
      this.currentPage = 1;
      this.loadData();
    });

    if (roleSubscription) {
      this.subscriptions.push(roleSubscription);
    }

    const datesSubscription = this.form.get('dates')?.valueChanges.subscribe(dates => {
      this.minDate = dates[0];
      this.maxDate = dates[1];
      this.currentPage = 1;
      this.loadData();
    });

    if (datesSubscription) {
      this.subscriptions.push(datesSubscription);
    }
  }

  private loadRole(): void {
    const roleSubscription = this.userService.currentRole.subscribe(role => {
      if (role.type === UserRoleType.admin) {
        this.userCanChooseSelectedRole = true;
      }
    });

    if (roleSubscription) {
      this.subscriptions.push(roleSubscription);
    }
  }

  private setTitle(): void {
    this.titleService.setTitle('Lavéo - Rapport : Monitoring Flotte');
  }
}
